/**
 * Staff session, shared by the ops and onsite apps. In the cloud, Identity Platform signs staff
 * in (Google, or an email link for anyone without a Google account) and the invite-only blocking
 * function stamps `role` and `tenantId` claims; Firebase refreshes the ID token hourly and we pick up each new one. Locally,
 * `POST /dev/login` on the dev gateway mints the same claims for anyone on the Staff list.
 */
import { ApiClient } from '../api/client';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Role = 'owner' | 'crew';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
const K_TOKEN = 'pmh.ops.token'; // per origin, so ops and onsite each keep their own
export const DEV_LOGIN = import.meta.env.DEV || import.meta.env.VITE_DEV_LOGIN === '1';

interface Claims {
  email: string;
  role: Role;
}

function decode(token: string | null): Claims | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob((token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/'))) as Partial<Claims> & { exp?: number };
    if (!payload.role || !payload.email) return null;
    if (payload.exp && payload.exp * 1000 < Date.now()) return null;
    return { email: payload.email, role: payload.role };
  } catch {
    return null;
  }
}

export function currentToken(): string | null {
  return readToken();
}

function readToken(): string | null {
  try {
    return sessionStorage.getItem(K_TOKEN);
  } catch {
    return null;
  }
}
function writeToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(K_TOKEN, token);
    else sessionStorage.removeItem(K_TOKEN);
  } catch {
    /* private mode: the session lasts as long as the tab */
  }
}

interface SessionState {
  claims: Claims | null;
  api: ApiClient;
  devSignIn: (email: string) => Promise<void>;
  googleSignIn: () => Promise<void>;
  emailLinkSignIn: (email: string) => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(readToken);
  const claims = useMemo(() => decode(token), [token]);

  const accept = useCallback((t: string | null) => {
    writeToken(t);
    setToken(t);
  }, []);

  const api = useMemo(
    () => new ApiClient({ baseUrl: API_BASE, getToken: () => readToken(), onUnauthorized: () => accept(null) }),
    [accept],
  );

  // Cloud: keep the token in step with Firebase (it refreshes before the hour is up).
  useEffect(() => {
    if (DEV_LOGIN) return;
    let stop = () => {};
    void import('./identity').then(({ onToken, completeEmailLink }) => {
      void completeEmailLink();
      stop = onToken(accept);
    });
    return () => stop();
  }, [accept]);

  const devSignIn = useCallback(
    async (email: string) => {
      const res = await api.post<{ token: string }>('/dev/login', { email });
      accept(res.token);
    },
    [api, accept],
  );
  const googleSignIn = useCallback(async () => {
    const { google } = await import('./identity');
    await google();
  }, []);
  const emailLinkSignIn = useCallback(async (email: string) => {
    const { sendLink } = await import('./identity');
    await sendLink(email);
  }, []);
  const signOut = useCallback(() => {
    accept(null);
    if (!DEV_LOGIN) void import('./identity').then(({ signOutAll }) => signOutAll());
  }, [accept]);

  const value = useMemo(
    () => ({ claims, api, devSignIn, googleSignIn, emailLinkSignIn, signOut }),
    [claims, api, devSignIn, googleSignIn, emailLinkSignIn, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
